/**
 * asaasDisclosure — a atribuição obrigatória ao Asaas, num lugar só.
 *
 * O app opera no modelo **BaaS (Banking as a Service)** do Asaas: quem presta o
 * serviço financeiro (conta de pagamento, cobrança, transferência) é o Asaas, e
 * o Orçamigo é apenas o integrador tecnológico. A Resolução Conjunta nº 16/2025
 * do Banco Central EXIGE que o cliente final saiba disso em cada ponto de
 * contato onde há movimentação ou gestão de valores — telas, documentos legais
 * e comprovantes.
 *
 * Isso é REGULAÇÃO, não política comercial: não se contorna com engenharia, e
 * "marca branca total" (esconder o Asaas) é proibida (#165b). O alcançável é
 * experiência própria + atribuição, que é exatamente o que este módulo entrega.
 *
 * **Por que os textos e as URLs moram aqui e não espalhados pelas telas:** são
 * cinco pontos de contato hoje (dois cards de pagamento, os Termos de Uso, o
 * PDF do documento e a página pública do orçamento) e a razão social do Asaas
 * aparece em três deles. Escritos em cada lugar, o primeiro ajuste de texto
 * pegaria um e deixaria os outros divergindo — a família #33/#62, agora sobre
 * uma obrigação regulatória, onde divergir custa mais do que um bug.
 */

/**
 * A razão social completa da instituição, como o playbook a escreve.
 *
 * Vai em todo texto de responsabilidade. Não abrevie para "Asaas" onde o papel
 * de instituição de pagamento está sendo declarado: o que a norma cobra é a
 * identificação de QUEM responde regulatoriamente.
 */
export const ASAAS_LEGAL_NAME = 'Asaas Gestão Financeira Instituição de Pagamentos S.A.';

/** O site institucional — destino do selo clicável. */
export const ASAAS_SITE_URL = 'https://asaas.com';

/**
 * As três variantes homologadas do selo, hospedadas na CDN do Asaas.
 *
 * ⚠️ **A URL é obrigatória; NÃO baixe o arquivo para dentro do bundle.** O
 * playbook é explícito em duas coisas que dependem do carregamento remoto:
 *
 *   1. o Asaas atualiza a marca de forma centralizada, e um SVG embarcado
 *      congelaria a versão antiga sem ninguém perceber;
 *   2. o `id` da URL identifica ESTA aplicação, e o Asaas confere pelo
 *      `Referer` se o selo foi de fato carregado — por isso o `<img>` NÃO pode
 *      levar `referrerPolicy="no-referrer"` (ver `AsaasSeal`).
 *
 * Consequência assumida: **sem rede o selo não aparece**. É por isso que o
 * componente cai para a menção TEXTUAL, que cumpre a mesma exigência sem
 * depender da CDN — e é também a razão de o PDF (gerado no cliente, #13) usar
 * só texto.
 */
export const ASAAS_SEAL_URLS = {
  /** Fundo claro. O padrão do app. */
  positivo:
    'https://baas.asaas.com/selos/Servicos_financeiros_Asaas-Reduzida-Positivo.svg?id=9f27699d-859c-4686-af77-718994ecc97a',
  /** Fundo escuro, traço preto. */
  negativoPreto:
    'https://baas.asaas.com/selos/Servicos_financeiros_Asaas-Reduzida-Negativo-Preto.svg?id=9f27699d-859c-4686-af77-718994ecc97a',
  /** Fundo escuro/colorido, traço branco. */
  negativoBranco:
    'https://baas.asaas.com/selos/Servicos_financeiros_Asaas-Reduzida-Negativo-Branco.svg?id=9f27699d-859c-4686-af77-718994ecc97a',
} as const;

export type AsaasSealVariant = keyof typeof ASAAS_SEAL_URLS;

/** Dimensões recomendadas pelo playbook, em pixels. */
export const ASAAS_SEAL_SIZE = { width: 160, height: 48 } as const;

/** O texto alternativo do selo — também o que o leitor de tela anuncia. */
export const ASAAS_SEAL_ALT = 'Serviços financeiros Asaas';

/**
 * A menção textual curta, para onde o selo não cabe ou não carrega.
 *
 * Usada como fallback do `<img>` (offline) e no rodapé do bloco de Pix do PDF.
 */
export const ASAAS_SHORT_DISCLOSURE =
  `Serviços financeiros e de pagamentos prestados por ${ASAAS_LEGAL_NAME}, ` +
  'instituição de pagamento autorizada pelo Banco Central do Brasil.';

/**
 * A versão de UMA LINHA, para o rodapé do PDF, onde a largura é fixa.
 *
 * O jsPDF não quebra linha sozinho (#13) e o bloco de Pix tem altura fixa, então
 * a menção do papel precisa caber inteira numa linha de fonte 6.
 */
export const ASAAS_PDF_DISCLOSURE = `Pagamento processado por ${ASAAS_LEGAL_NAME}`;

/**
 * A cláusula dos Termos de Uso, no texto do modelo fornecido pelo Asaas.
 *
 * ⚠️ **Não reescreva.** O playbook proíbe ao tomador "alterar ou criar
 * documentos legais relacionados ao processamento financeiro de pagamentos" —
 * o texto é do Asaas, e melhorar a redação aqui é uma violação, não um
 * refinamento. Os `NOME DA TOMADORA` do modelo já vêm preenchidos.
 */
export const ASAAS_CONTRACT_CLAUSE = [
  'Os serviços financeiros e de pagamentos disponibilizados por meio da presente plataforma, ' +
    'incluindo abertura e manutenção de conta de pagamento, processamento de transações, emissão ' +
    'de boletos, transferências, pagamentos e demais movimentações de valores, são prestados pelo ' +
    `${ASAAS_LEGAL_NAME.toUpperCase()}, instituição de pagamento autorizada a funcionar pelo Banco ` +
    'Central do Brasil.',
  'O Orçamigo atua exclusivamente como integradora tecnológica e distribuidora da experiência do ' +
    'produto, não sendo instituição financeira ou de pagamento, nem realizando intermediação ' +
    'financeira em nome próprio.',
  'O cliente declara ciência de que o relacionamento financeiro/de pagamentos e a responsabilidade ' +
    `regulatória pelos serviços acima descritos são do ${ASAAS_LEGAL_NAME.toUpperCase()}, nos termos ` +
    'da regulamentação vigente.',
] as const;

/**
 * Os canais de suporte do ASAAS — não os do Orçamigo.
 *
 * Exigência própria do playbook: o cliente final precisa saber que pode acionar
 * o Asaas diretamente para o que é da operação financeira. Mandá-lo ao suporte
 * do app para um problema de conta de pagamento é justamente o que a norma quer
 * evitar, porque o app não pode resolver (nem enxergar) o que é regulatório.
 */
export const ASAAS_SUPPORT = {
  phone: '0800 009 0037',
  phoneNote: 'somente Pessoa Jurídica',
  email: 'contato@asaas.com.br',
} as const;
